import { useMemo } from 'react';

import { type UseQueryOptions } from '@tanstack/react-query';
import { type LWApiPublicNetworkDnsReverseAssignableIpsResultRaw } from '@liquidweb-dev/club-sauce/public/network/dns/reverse/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import useDnsReverseAssignableIps from 'modules/queries/network/dns/reverse/useAssignableIps';

/** Gets a list of subaccounts that have assignable ips available for reverse DNS. Also returns the assignable ips for the provided uniqId. If no uniqId is provided, it will use the first domain in the list */
const useReverseDns = (
	{
		uniqId,
	}: {
		/** the uniq id of the domain to get the reverse dns for. If not provided, it will return the available ips for the first domain in the list */
		uniqId?: string;
	},
	options?: UseQueryOptions<
		LWApiPublicNetworkDnsReverseAssignableIpsResultRaw,
		ApiErrorI
	>,
) => {
	const { data: assignableIpsData, ...rest } =
		useDnsReverseAssignableIps(options);

	/** A list of subaccnts that have assignable ips */
	const domains = useMemo(() => {
		return assignableIpsData?.items.reduce(
			(accumulator, current) => {
				if (accumulator.every((item) => item.uniq_id !== current.uniq_id)) {
					accumulator.push({
						uniq_id: current.uniq_id,
						domain: current.domain,
					});
				}

				return accumulator;
			},
			[] as { uniq_id: string; domain: string }[],
		);
	}, [assignableIpsData?.items]);

	const selectedUniqId = uniqId || domains?.[0]?.uniq_id;

	/** A list of assignable ips for the selected domain. Sorted by whether or not they have a current ptr */
	const assignableIps = useMemo(() => {
		return assignableIpsData?.items
			.filter((item) => item.uniq_id === selectedUniqId)
			.sort((a, b) => {
				if (a.current_ptr && !b.current_ptr) return -1;
				if (!a.current_ptr && b.current_ptr) return 1;
				return 0;
			});
	}, [assignableIpsData?.items, selectedUniqId]);

	return { assignableIps, domains, selectedUniqId, ...rest };
};

export default useReverseDns;
